import React, { useState, useEffect } from 'react';
import { Choir, ChoirImage } from '../../types/Choir';
import { AdminFormProps } from '../shared/AdminInterfaces';
import ChoirService from '../../services/ChoirService';
import { Upload, X } from 'lucide-react';

interface ChoirFormData {
  name: string;
  description: string;
  skillLevel?: string;
  auditionRequired: boolean;
  category?: string;
  memberCount?: number;
  testimonialText?: string;
  testimonialAuthor?: string;
  isActive: boolean;
}

const ChoirForm: React.FC<AdminFormProps<Choir, ChoirFormData>> = ({
  item,
  isOpen,
  onSubmit,
  onClose,
  isLoading = false
}) => {
  const [formData, setFormData] = useState<ChoirFormData>({
    name: '',
    description: '',
    skillLevel: '',
    auditionRequired: false,
    category: '',
    memberCount: undefined,
    testimonialText: '',
    testimonialAuthor: '',
    isActive: true
  });

  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [existingImages, setExistingImages] = useState<ChoirImage[]>([]);

  // Reset form when item changes
  useEffect(() => {
    if (item) {
      setFormData({
        name: item.name,
        description: item.description,
        skillLevel: item.skillLevel || '',
        auditionRequired: item.auditionRequired || false,
        category: item.category || '',
        memberCount: item.memberCount,
        testimonialText: item.testimonialText || '',
        testimonialAuthor: item.testimonialAuthor || '',
        isActive: item.isActive || true
      });
      setExistingImages(item.images || []);
    } else {
      // Reset for create mode
      setFormData({
        name: '',
        description: '',
        skillLevel: '',
        auditionRequired: false,
        category: '',
        memberCount: undefined,
        testimonialText: '',
        testimonialAuthor: '',
        isActive: true
      });
      setExistingImages([]);
    }
  }, [item]);

  useEffect(() => {
    // Generate image previews
    const previews = imageFiles.map(file => URL.createObjectURL(file));
    setImagePreviews(previews);

    // Clean up URLs on unmount
    return () => {
      previews.forEach(url => URL.revokeObjectURL(url));
    };
  }, [imageFiles]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      const newFiles = Array.from(files);
      setImageFiles(prev => [...prev, ...newFiles].slice(0, 5)); // Limit to 5 images
    }
  };

  const handleImageRemove = (index: number) => {
    setImageFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleExistingImageRemove = async (imageId: string) => {
    try {
      await ChoirService.deleteChoirImage(item!.id, imageId);
      setExistingImages(prev => prev.filter(img => img.id !== imageId));
    } catch (error) {
      console.error('Error removing image:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      // Pass both form data and image files to the parent component
      await onSubmit(formData, imageFiles.length > 0 ? imageFiles : undefined);

      // Clear the files after successful submission
      setImageFiles([]);
      setImagePreviews([]);
    } catch (error) {
      console.error('Error submitting form:', error);
      throw error; // Re-throw to let the parent component handle the error
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold mb-4">
          {item ? 'Edit Choir' : 'Add New Choir'}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Choir Name</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              required
              rows={3}
              className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Skill Level</label>
              <select
                name="skillLevel"
                value={formData.skillLevel}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select skill level</option>
                <option value="Beginner-Friendly">Beginner-Friendly</option>
                <option value="Intermediate">Intermediate</option>
                <option value="Advanced">Advanced</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Category</label>
              <select
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select category</option>
                <option value="Mixed">Mixed Choir</option>
                <option value="Women's">Women's Choir</option>
                <option value="Men's">Men's Choir</option>
                <option value="Chamber">Chamber Choir</option>
                <option value="Show">Show Choir</option>
              </select>
            </div>
          </div>

          <div className="space-y-3">
            <label className="flex items-center">
              <input
                type="checkbox"
                name="auditionRequired"
                checked={formData.auditionRequired}
                onChange={handleInputChange}
                className="mr-2 rounded border-gray-300 focus:ring-blue-500"
              />
              <span className="text-sm font-medium">Audition Required</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                name="isActive"
                checked={formData.isActive}
                onChange={handleInputChange}
                className="mr-2 rounded border-gray-300 focus:ring-blue-500"
              />
              <span className="text-sm font-medium">Active</span>
            </label>
          </div>

          {/* Image Upload Section */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Choir Images
            </label>

            {/* Image Guidelines */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
              <h4 className="text-sm font-medium text-blue-800 mb-2">📸 Standardized Image Guidelines:</h4>
              <ul className="text-xs text-blue-700 space-y-1">
                <li>• <strong>Aspect Ratio:</strong> 3:2 (landscape) - REQUIRED for consistency</li>
                <li>• <strong>Recommended Size:</strong> 1200x800px for best quality</li>
                <li>• <strong>Minimum Size:</strong> 600x400px</li>
                <li>• <strong>Format:</strong> JPG or PNG files only</li>
                <li>• <strong>File Size:</strong> Under 3MB per image</li>
                <li>• <strong>Limit:</strong> Maximum 5 images per choir</li>
                <li>• <strong>Examples:</strong> Group photos, performance shots, or choir activities cropped to 3:2 ratio</li>
                <li>• <strong>Tips:</strong> Use photo editing software to crop to exact 3:2 ratio before uploading</li>
              </ul>
            </div>

            {/* Existing Images */}
            {existingImages.length > 0 && (
              <div className="mb-4">
                <h5 className="text-sm font-medium text-gray-700 mb-2">Current Images</h5>
                <div className="flex flex-wrap gap-2">
                  {existingImages.map((image) => {
                    // Get image URL - use S3 URL if available, fallback to base64 for legacy images
                    const getImageUrl = (img: ChoirImage): string => {
                      if (img.s3Url) {
                        return img.s3Url;
                      }
                      // Fallback for legacy base64 images (stored in s3Key field for backward compatibility)
                      if (img.s3Key && img.s3Key.startsWith('data:')) {
                        return img.s3Key;
                      }
                      return '';
                    };

                    const imageUrl = getImageUrl(image);
                    if (!imageUrl) return null;

                    return (
                      <div key={image.id} className="relative">
                        <img
                          src={imageUrl}
                          alt="Choir"
                          className="w-24 h-16 object-cover rounded-md border border-gray-300"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => handleExistingImageRemove(image.id)}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                        >
                          <X size={12} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* New Image Previews */}
            <div className="flex flex-wrap gap-2">
              {imagePreviews.map((preview, index) => (
                <div key={index} className="relative">
                  <img
                    src={preview}
                    alt={`Preview ${index + 1}`}
                    className="w-24 h-16 object-cover rounded-md border border-gray-300"
                  />
                  <button
                    type="button"
                    onClick={() => handleImageRemove(index)}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}

              {/* Upload Button */}
              {(existingImages.length + imageFiles.length) < 5 && (
                <label className="flex flex-col items-center justify-center w-24 h-16 border-2 border-dashed border-gray-300 rounded-md cursor-pointer hover:bg-gray-50 hover:border-blue-400 transition-colors">
                  <Upload size={20} className="text-gray-400" />
                  <span className="text-xs text-gray-500">Add Image</span>
                  <input
                    type="file"
                    accept="image/jpeg,image/jpg,image/png"
                    onChange={handleImageChange}
                    className="hidden"
                    multiple
                  />
                </label>
              )}
            </div>

            <p className="text-xs text-gray-500 mt-2">
              {existingImages.length + imageFiles.length}/5 images uploaded
            </p>
          </div>

          {/* Additional Form Fields */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Member Count (Optional)</label>
              <input
                type="number"
                name="memberCount"
                value={formData.memberCount || ''}
                onChange={handleInputChange}
                min="1"
                className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="e.g. 35"
              />
            </div>
          </div>

          {/* Testimonial Section */}
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-gray-700">Student/Parent Testimonial (Optional)</h4>
            <div>
              <label className="block text-sm font-medium mb-1">Testimonial Quote</label>
              <textarea
                name="testimonialText"
                value={formData.testimonialText}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="A quote from a student or parent about their experience..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Testimonial Author</label>
              <input
                type="text"
                name="testimonialAuthor"
                value={formData.testimonialAuthor}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="e.g. Sarah M., Parent or Alex J., Student"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {isLoading ? 'Saving...' : (item ? 'Update Choir' : 'Create Choir')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChoirForm;
